import axios from 'axios';

const getYakiAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${username}:${password}`)}`;
};

// Logs the server-computed cost and returns the assistant reply text.
function extractYakiResponse(data) {
    if (!data) return '';
    if (data.request_cost || data.cumulative_cost) {
        console.log(
            `Yaki cost — request: ${data.request_cost}, cumulative (assessment): ${data.cumulative_cost}`
        );
    }
    return Array.isArray(data.response) ? (data.response[0] || '') : (data.response || '');
}

/**
 * Backend-owned prompt path (Integration Plan §15 #1): the client sends a
 * structured spec; the backend (YakiPromptService) assembles the prompt,
 * generates, and accumulates cost. Used for the plain-text MCQ path; the
 * script / image / pdf paths still use the client templates via
 * GenerateQuestions until those variants are wired end-to-end.
 */
async function GenerateQuestionsFromSpec(spec, context = {}) {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const body = {
            ...spec,
            assessmentId: context.assessmentId || null,
            courseId: context.courseId || null,
            courseName: context.courseName || null,
            assessmentName: context.assessmentName || null,
            userId: localStorage.getItem('userId'),
            userName: localStorage.getItem('username'),
            model: context.model || null
        };

        const response = await axios.post(
            `${apiUrl}/api/v1/openai/yaki/generate-from-spec`,
            body,
            {
                timeout: 150000, // don't let a slow/hung backend block the UI forever
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getYakiAuthHeader()
                }
            }
        );

        return extractYakiResponse(response.data);
    } catch (error) {
        console.error('Error in yaki/generate-from-spec:', error);
        return '';
    }
}

/**
 * Asks the backend to assemble (but not run) the prompt from a spec — powers
 * the "Show Prompt" / "Edit Prompt" UI now that wording is owned server-side.
 */
async function PreviewYakiPrompt(spec) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await axios.post(
        `${apiUrl}/api/v1/openai/yaki/preview-prompt`,
        spec,
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: getYakiAuthHeader()
            }
        }
    );
    return response.data?.prompt || '';
}

/**
 * Learner-adaptive generation (Integration Plan §9). Fetches the learner's
 * signals (weak domains, Bloom growth edge, progress) for preview, and runs the
 * adaptive generation which reweights/targets the set on the backend.
 */
async function getLearnerSignals(userId, courseId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.get(`${apiUrl}/api/v1/openai/yaki/learner-signals`, {
        params: { userId, courseId },
        headers: { Authorization: getYakiAuthHeader() }
    });
    return res.data || {};
}

async function GenerateAdaptiveQuestions(spec) {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const res = await axios.post(
            `${apiUrl}/api/v1/openai/yaki/generate-adaptive`,
            spec,
            { headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() } }
        );
        return extractYakiResponse(res.data);
    } catch (error) {
        console.error('Error in yaki/generate-adaptive:', error);
        return '';
    }
}

/**
 * Question-bank planning + generation (Integration Plan §4). getBankPlan is a
 * fast preview (no LLM); GenerateBanks runs the full master-pool orchestration
 * (one assistant call per domain bank + per exam set) and is long-running.
 */
async function getBankPlan(request) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.post(`${apiUrl}/api/v1/openai/yaki/bank-plan`, request, {
        headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() }
    });
    return res.data || {};
}

async function GenerateBanks(request) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.post(`${apiUrl}/api/v1/openai/yaki/generate-banks`, request, {
        headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() }
    });
    return res.data || {};
}

// Async variant: returns a jobId to poll via getBankJob.
async function StartBankGeneration(request) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.post(`${apiUrl}/api/v1/openai/yaki/generate-banks-async`, request, {
        headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() }
    });
    return res.data || {};
}

async function getBankJob(jobId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.get(`${apiUrl}/api/v1/openai/yaki/bank-job/${jobId}`, {
        headers: { Authorization: getYakiAuthHeader() }
    });
    return res.data || {};
}

// Draft-assessment review/activation (Integration Plan §4).
async function getDraftAssessments(courseId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.get(`${apiUrl}/api/v1/openai/yaki/draft-assessments`, {
        params: { courseId },
        headers: { Authorization: getYakiAuthHeader() }
    });
    return res.data || [];
}

async function activateAssessment(testId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    await axios.put(`${apiUrl}/api/update-assessment-active-status`,
        { testId, active: 1 },
        { headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() } }
    );
}

async function bulkActivateAssessments(testIds) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const res = await axios.post(`${apiUrl}/api/v1/openai/yaki/activate-assessments`,
        testIds,
        { headers: { 'Content-Type': 'application/json', Authorization: getYakiAuthHeader() } }
    );
    return res.data || {};
}

async function GenerateQuestions(prompt, testId, finalPassage, caseStudyOption, mediaUrl, directAttachmentType, context = {}) {

    const apiUrl = process.env.REACT_APP_API_URL;
    const apiUrl1 = process.env.REACT_APP_API_URL1;
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || '';

    const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
    };

    const isImageCase =
        caseStudyOption === 'generateImage' ||
        (mediaUrl && directAttachmentType === 'image');

    // ================= IMAGE BASED =================
    if(directAttachmentType==='pdf')
    {
        try {
                const downloadResponse = await axios.get(`${apiUrl}/api/download-pdf`, {
                    params: { pdfUrl: mediaUrl },
                    headers: { Authorization: getBasicAuthHeader() },
                    responseType: "blob",
                });
        
                const pdfBlob = new Blob([downloadResponse.data], { type: 'application/pdf' });
                const pdfFile = new File([pdfBlob], "document.pdf", { type: 'application/pdf' });
        
                const formData = new FormData();
                formData.append("file", pdfFile);
                formData.append("prompt", prompt);
                formData.append("userId", userId);

                console.log(mediaUrl,prompt)
        
                const response = await axios.post(`${apiUrl}/api/v1/openai/process-pdf`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': getBasicAuthHeader()
                    }
                });
        
                //const extractedText = response.data.response[0]; 
                
                return response.data.response[0];
        
            } catch (error) {
                //console.error("Error processing PDF:", error);
                //setAlertMessage("Failed to extract passage from PDF. Please try again.");
                throw error; 
            }
    }
    else{
    if (isImageCase) {
        try {

            const body = {
                prompt: prompt,
                imageUrl: mediaUrl,
                userId,
            };

            const response = await axios.post(
                `${apiUrl}/api/v1/openai/send-prompt-with-image`,
                body,
                {
                    headers: {
                        'Authorization': getBasicAuthHeader(),
                        'Content-Type': 'application/json',
                    },
                }
            );

            return response.data?.response || null;

        } catch (error) {
            console.error('Error in send-prompt-with-image:', error);
            return null;
        }

    } else {

        // ================= TEXT BASED (Yaki) =================
        try {

            const body = {
                prompt,
                assessmentId: testId,
                courseId: context.courseId || null,
                courseName: context.courseName || null,
                assessmentName: context.assessmentName || null,
                userId: localStorage.getItem('userId'),
                userName: localStorage.getItem('username'),
                model: context.model || null
            };

            const response = await axios.post(
                `${apiUrl}/api/v1/openai/yaki/generate`,
                body,
                {
                    timeout: 150000, // don't let a slow/hung backend block the UI forever
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': getBasicAuthHeader()
                    },
                }
            );

            const data = response.data || {};

            // Cost for this request is computed and accumulated server-side;
            // surface the running total so the UI/console can track spend.
            if (data.request_cost || data.cumulative_cost) {
                console.log(
                    `Yaki cost — request: ${data.request_cost}, cumulative (assessment): ${data.cumulative_cost}`
                );
            }

            return Array.isArray(data.response)
                ? (data.response[0] || '')
                : (data.response || '');

        } catch (error) {
            console.error('Error in yaki/generate:', error);
            return '';
        }
    }
}
}

export {
    GenerateQuestions,
    GenerateQuestionsFromSpec,
    PreviewYakiPrompt,
    getLearnerSignals,
    GenerateAdaptiveQuestions,
    getBankPlan,
    GenerateBanks,
    StartBankGeneration,
    getBankJob,
    getDraftAssessments,
    activateAssessment,
    bulkActivateAssessments
};